<template>
  <div class="col-sm-12">
    <div class="card">
      <!-- Single row for all controls -->
      <div class="dropdown-item page-item">
        <div class="page-grid">
          <!-- For non-distributor users: show dropdown, search bar, and create button -->
          <template v-if="!isDistributorUser">
            <!-- Distributor Dropdown -->
            <div class="page-search-container dropdown-item">
              <div style="display: flex; align-items: center; gap: 10px;">
                <label for="distributor" style="font-weight:700; white-space: nowrap;">Distributor : </label>
                <select
                    id="title"
                    v-model="selectedDistributor"
                    class="scrollable-dropdown select-distributor"
                    @change="onDistributorChange"
                >
                  <option disabled value="">Select Distributor</option>
                  <option v-for="distributor in distributorList" :key="distributor.profile_id" :value="distributor.profile_id">
                    {{ distributor.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Search Bar -->
            <div class="page-search-container dropdown-item">
              <input
                  type="text"
                  placeholder="Search dealers by name, mobile"
                  class="search-input"
                  v-model="searchFilter"
              />
            </div>
          </template>

          <!-- For distributor users: show only search bar -->
          <template v-else>
            <!-- Search Bar -->
            <div class="page-search-container dropdown-item">
              <input
                  type="text"
                  placeholder="Search dealers by name, mobile"
                  class="search-input"
                  v-model="searchFilter"
              />
            </div>
          </template>
        </div>
      </div>

      <div v-if="loading" class="loading-spinner">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <div class="table-responsive" v-if="dealerList.length > 0">
        <table class="table">
          <thead>
          <tr class="border-bottom-primary">
            <th scope="col">Name</th>
            <th scope="col">Category</th>
            <th scope="col">Mobile Number</th>
            <th scope="col">Status</th>
            <th scope="col">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="dealer in filteredDealers" :key="dealer.id">
            <td>{{ dealer.name}}</td>
            <td>{{ dealer.dealer_cat_name}}</td>
            <td>{{ dealer.mobile }}</td>
            <td>
                <span :class="{
                  'status-label status-yes': dealer.is_active === '1',
                  'status-label status-no': dealer.is_active === '0'
                }">
                  {{ dealer.is_active === '1' ? 'Active' : 'Inactive' }}
                </span>
            </td>
            <td>
              <i
                class="fa action-icon"
                :class="dealer.is_eligible_trust == 1
                  ? 'fa-undo action-revoke'
                  : 'fa-check-circle action-enable'"
                :title="dealer.is_eligible_trust == 1 ? 'Revoke Trust' : 'Enable Trust'"
                @click="editTrustEligibility(dealer)"
              ></i>
              <i
                class="fa fa-external-link-alt action-icon action-issue"
                title="Issue Trust"
                @click="openTrustIssueForm(dealer.id)"
              ></i>
              <i
                class="fa fa-eye action-icon action-history"
                title="View History"
                @click="openTrustHistory(dealer.profile_id)"
              ></i>
            </td>
          </tr>
          <tr v-if="filteredDealers.length === 0">
            <td colspan="5" class="text-center">No dealers found matching your search</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="!loading && selectedDistributor" class="no-data-message">
        No dealers found for the selected distributor.
      </div>

      <div v-else-if="!loading && isDistributorUser && dealerList.length === 0" class="no-data-message">
        No dealers found for your account.
      </div>
    </div>
  </div>

  <!-- Trust Eligibility Confirmation Modal -->
  <div v-if="showTrustModal" class="status-modal-overlay">
    <div class="status-modal">
      <div class="status-modal-icon" :class="pendingTrustValue === 1 ? 'activate' : 'deactivate'">
        <i
          :class="pendingTrustValue === 1
            ? 'fa fa-check-circle'
            : 'fa fa-exclamation-triangle'"
        ></i>
      </div>

      <h4>
        {{ pendingTrustValue === 1 ? 'Enable Trust Eligibility' : 'Revoke Trust Eligibility' }}
      </h4>

      <p>
        Are you sure you want to
        <strong>{{ pendingTrustValue === 1 ? 'mark' : 'revoke trust eligibility for' }}</strong>
        "{{ selectedDealer?.name }}"{{ pendingTrustValue === 1 ? ' as trust eligible?' : '?' }}
      </p>

      <div class="status-modal-actions">
        <button type="button" class="btn btn-secondary" @click="closeTrustModal">
          Cancel
        </button>

        <button
          type="button"
          :class="pendingTrustValue === 1 ? 'btn btn-success' : 'btn btn-danger'"
          @click="confirmTrustChange"
          :disabled="trustUpdating"
        >
          <i v-if="trustUpdating" class="fa fa-spinner fa-spin"></i>
          {{ trustUpdating ? 'Updating...' : (pendingTrustValue === 1 ? 'Enable' : 'Revoke') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import {
  fetchTrustEligibleDealersByDistributors,
  fetchDistributorsList,
  updateDealerTrustEligibility
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: 'DealerList',
  data() {
    return {
      loading: false,
      token: localStorage.getItem("token_set") || "",
      profile_id: localStorage.getItem("profile_id"),
      dealerList: [],
      distributorList: [],
      selectedDistributor: "",
      searchFilter: "",
      isDistributorUser: false,

      showTrustModal: false,
      selectedDealer: null,
      pendingTrustValue: null,
      trustUpdating: false,
    }
  },

  computed: {
    // perform search
    filteredDealers() {
      return [...this.dealerList]
          .filter(dealer =>
              (dealer.name || '').toLowerCase().includes(this.searchFilter.toLowerCase()) ||
              (dealer.mobile || '').includes(this.searchFilter)
          );
    }
  },
  methods: {
    // fetch distributor list
    async fetchDistributors() {
      try {
        const response = await fetchDistributorsList(this.profile_id, this.token);
        this.distributorList = response.data.data || [];
        console.log('Fetched Distributors:', this.distributorList);
      } catch (error) {
        console.error('Error fetching Distributors:', error);
      }
    },

    async getDealers() {
      try {
        this.loading = true;
        this.dealerList = [];

        if (!this.selectedDistributor) {
          console.warn("No distributor selected.");
          return;
        }
        const response = await fetchTrustEligibleDealersByDistributors(this.selectedDistributor, this.token);
        this.dealerList = response.data?.data || [];
        console.log('Fetched Dealers:', this.dealerList);
      } catch (error) {
        console.error("Error fetching dealer list:", error);
        this.dealerList = [];
      } finally {
        this.loading = false;
      }
    },

    onDistributorChange() {
      if (this.selectedDistributor) {
        this.getDealers();
      }
    },

    editTrustEligibility(dealer) {
      this.selectedDealer = dealer;
      this.pendingTrustValue = dealer.is_eligible_trust == 1 ? 0 : 1;
      this.showTrustModal = true;
    },

    closeTrustModal() {
      this.showTrustModal = false;
      this.selectedDealer = null;
      this.pendingTrustValue = null;
    },

    async confirmTrustChange() {
      if (!this.selectedDealer || this.pendingTrustValue === null) {
        return;
      }
      await this.applyTrustEligibility(this.selectedDealer, this.pendingTrustValue);
    },

    async applyTrustEligibility(dealer, newValue) {
      try {
        this.trustUpdating = true;

        const response = await updateDealerTrustEligibility(dealer.id, this.token, { is_eligible_trust: newValue });

        if (response.data.status === "success") {
          dealer.is_eligible_trust = newValue;
          this.toast.success(
            response.data.message ||
            `Trust eligibility ${newValue === 1 ? 'enabled' : 'revoked'} successfully.`
          );
          this.closeTrustModal();
          await this.getDealers();
        } else {
          this.toast.error(response.data.message || "Failed to update trust eligibility.");
        }
      } catch (error) {
        console.error("Error updating trust eligibility:", error);
        this.toast.error(
          error.response?.data?.message || "Failed to update trust eligibility."
        );
      } finally {
        this.trustUpdating = false;
      }
    },

    // Initialize user type and auto-load data for distributors
    initializeUserSettings() {
      const userLabel = localStorage.getItem("user_label");
      this.isDistributorUser = userLabel === "DISTRIBUTOR" || userLabel === "DISTRIBUTOR BACK OFFICE";

      if (this.isDistributorUser) {
        // For distributor users, automatically set selected distributor and load dealers
        this.selectedDistributor = userLabel === "DISTRIBUTOR BACK OFFICE" ? localStorage.getItem("reportingL1ProfileId") : this.profile_id;
        this.getDealers();
      }
    },

    openTrustIssueForm(dealerId) {
      this.$router.push({ path: `/issue-trust/${dealerId}` });
    },

    openTrustHistory(dealerId) {
      this.$router.push({ path: `/trust-history/${dealerId}` });
    },
  },

  setup() {
    const router = useRouter();
    const toast = useToast();
    return { router, toast };
  },
  mounted() {
    this.initializeUserSettings();

    if (!this.isDistributorUser) {
      this.fetchDistributors();
    }
  }
};
</script>

<style scoped lang="scss">
/* Apply the same styling pattern as item screen */
.page-grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 15px;
}

.page-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}

.page-search-container {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.page-search-container.dropdown-item {
  flex-grow: 0;
  flex-basis: auto;
}

.search-input {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  width: 100%;
  min-width: 250px;
}

.search-input:hover {
  border-color: #0056b3;
}

.new-item-btn {
  margin-left: auto;
  padding: 10px 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

.select-distributor {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  min-width: 250px;
}

/* Keep the rest of your existing dealer screen styles */
.card {
  padding: 10px;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

.fa-shield {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

i:hover {
  color: #0056b3;
  transform: scale(1.2);
}

i:active {
  transform: scale(1.1);
}

.action-icon {
  cursor: pointer;
  margin-right: 14px;
  font-size: 16px;
  transition: color 0.3s, transform 0.3s;
}

.action-revoke { color: #dc2626; }
.action-revoke:hover { color: #b91c1c; }

.action-enable { color: #069e27; }
.action-enable:hover { color: #047a1e; }

.action-issue { color: #1c66ad; }
.action-issue:hover { color: #155a9c; }

.action-history { color: #6b7280; }
.action-history:hover { color: #374151; }

.status-label {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 5px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 90px;
  max-width: 90px;
  text-align: center;
}

.status-yes {
  background-color: #069E27;
  color: #fff;
  border-radius: 4px;
}

.status-no {
  background-color: red;
  color: #fff;
  border-radius: 4px;
}

.no-data-message {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
}

.text-center {
  text-align: center;
}

.scrollable-dropdown {
  max-height: 200px;
  overflow-y: auto;
}

/* Focus states */
input:focus, select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.button-default {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.button-default:hover {
  color: #fff;
  background-color: #0056b3;
  border-color: #004085;
}

.status-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.status-modal {
  background: #fff;
  width: 420px;
  max-width: 90%;
  border-radius: 10px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2);
}

.status-modal-icon {
  width: 55px;
  height: 55px;
  margin: 0 auto 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.status-modal-icon.activate {
  background: #e8f7ed;
  color: #069e27;
}

.status-modal-icon.deactivate {
  background: #fdecec;
  color: #dc2626;
}

.status-modal h4 {
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 600;
}

.status-modal p {
  margin-bottom: 25px;
  color: #6c757d;
  font-size: 14px;
}

.status-modal-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.status-modal-actions button {
  min-width: 100px;
  padding: 8px 18px;
  border-radius: 5px;
  border: none;
}

.btn-success {
  background-color: #069e27;
  color: #fff;
}

.btn-success:hover {
  background-color: #047a1e;
}

.btn-danger {
  background-color: #dc2626;
  color: #fff;
}

.btn-danger:hover {
  background-color: #b91c1c;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #545b62;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-grid {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .page-search-container {
    width: 100%;
  }

  .search-input, .select-distributor {
    min-width: 100%;
  }

  .new-item-btn {
    margin-left: 0;
    width: 100%;
  }
}
</style>